import { useEffect, useMemo, useRef, useState } from 'react';
import CpmCheckbox from '../../../components/design-system/Checkbox';
import CpmChip from '../../../components/design-system/Chip';
import { STARTER_COPY } from '../starterCopy';

interface StateOption {
    id: string;
    name: string;
    count?: number;
}

interface StateMultiSelectProps {
    options: StateOption[];
    selectedIds: string[];
    onChange: (ids: string[]) => void;
    mode: 'filter' | 'editor';
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Shown in the field when the filter list is closed. */
    closedLabel?: string;
    disabled?: boolean;
}

function Chevron() {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ position: 'absolute', right: 13, top: 13, pointerEvents: 'none', color: 'var(--cpm-text-secondary)' }}
        >
            <polyline points="6 9 12 15 18 9" />
        </svg>
    );
}

export default function StateMultiSelect({
    options,
    selectedIds,
    onChange,
    mode,
    open,
    onOpenChange,
    closedLabel = '',
    disabled = false,
}: StateMultiSelectProps) {
    const rootRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [query, setQuery] = useState('');

    useEffect(() => {
        if (!open) return;
        const onDoc = (event: MouseEvent) => {
            const root = rootRef.current;
            if (!root || !root.contains(event.target as Node)) {
                onOpenChange(false);
                setQuery('');
            }
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open, onOpenChange]);

    useEffect(() => {
        const list = listRef.current;
        if (!open || !list) return;
        const onWheel = (event: WheelEvent) => {
            const up = event.deltaY < 0;
            const atTop = list.scrollTop <= 0;
            const atBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;
            if ((up && atTop) || (!up && atBottom)) {
                event.preventDefault();
            }
            event.stopPropagation();
        };
        list.addEventListener('wheel', onWheel, { passive: false });
        return () => list.removeEventListener('wheel', onWheel);
    }, [open, query, options.length]);

    const names = useMemo(() => {
        const map = new Map<string, string>();
        options.forEach((option) => {
            map.set(option.id, option.name);
        });
        return map;
    }, [options]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter((option) => option.name.toLowerCase().includes(q));
    }, [options, query]);

    const toggle = (id: string) => {
        if (selectedIds.includes(id)) {
            onChange(selectedIds.filter((value) => value !== id));
            return;
        }
        onChange([...selectedIds, id]);
    };

    const openList = () => {
        if (disabled) return;
        if (!open) {
            setQuery('');
            onOpenChange(true);
        }
        inputRef.current?.focus();
    };

    const editor = mode === 'editor';
    const countLabel = (() => {
        if (!editor) return '';
        const base = query.trim()
            ? `${filtered.length} of ${options.length} ${STARTER_COPY.STATES_WORD}`
            : `${options.length} ${STARTER_COPY.STATES_WORD}`;
        if (!selectedIds.length) return base;
        return `${base} · ${selectedIds.length} ${STARTER_COPY.PICKED}`;
    })();

    return (
        <div ref={rootRef} data-dd="states" style={{ position: 'relative' }}>
            {editor ? (
                <div
                    onMouseDown={(event) => {
                        if ((event.target as HTMLElement).closest('[data-chip-remove]')) return;
                        openList();
                    }}
                    style={{
                        minHeight: 42,
                        boxSizing: 'border-box',
                        padding: '5px 36px 5px 6px',
                        borderRadius: 10,
                        border: `1.5px solid ${open ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-strong)'}`,
                        background: '#fff',
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 5,
                        cursor: 'text',
                    }}
                >
                    {selectedIds.map((id) => (
                        <span key={id} data-chip-remove="true">
                            <CpmChip
                                variant="token"
                                onRemove={() => onChange(selectedIds.filter((value) => value !== id))}
                            >
                                {names.get(id) || id}
                            </CpmChip>
                        </span>
                    ))}
                    <input
                        ref={inputRef}
                        value={query}
                        disabled={disabled}
                        placeholder={selectedIds.length ? STARTER_COPY.ADD_STATE : STARTER_COPY.EVERY_STATE}
                        onFocus={openList}
                        onChange={(event) => {
                            setQuery(event.target.value);
                            if (!open) onOpenChange(true);
                        }}
                        style={{
                            flex: 1,
                            minWidth: 110,
                            height: 28,
                            padding: '0 7px',
                            border: 'none',
                            outline: 'none',
                            background: 'transparent',
                            fontSize: 13.5,
                            fontWeight: 700,
                            color: 'var(--cpm-text-primary)',
                        }}
                    />
                </div>
            ) : (
                <input
                    ref={inputRef}
                    value={open ? query : closedLabel}
                    disabled={disabled}
                    placeholder={closedLabel}
                    onMouseDown={openList}
                    onFocus={openList}
                    onChange={(event) => {
                        setQuery(event.target.value);
                        if (!open) onOpenChange(true);
                    }}
                    style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        height: 40,
                        padding: '0 36px 0 13px',
                        borderRadius: 10,
                        border: `1.5px solid ${open ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-strong)'}`,
                        background: '#fff',
                        fontSize: 13.5,
                        fontWeight: 700,
                        color: 'var(--cpm-text-primary)',
                        outline: 'none',
                    }}
                />
            )}
            <Chevron />
            {open ? (
                <div
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        left: 0,
                        right: 0,
                        zIndex: 70,
                        background: '#fff',
                        border: '1px solid var(--cpm-border-default)',
                        borderRadius: 12,
                        boxShadow: '0 16px 40px -12px rgba(14,8,88,.28)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    {editor ? (
                        <div
                            className="flex items-center justify-between"
                            style={{ gap: 8, padding: '10px 14px 8px', borderBottom: '1px solid var(--cpm-border-default)' }}
                        >
                            <span
                                style={{
                                    fontSize: 10.5,
                                    fontWeight: 800,
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                    color: 'var(--cpm-text-secondary)',
                                }}
                            >
                                {countLabel}
                            </span>
                            {selectedIds.length ? (
                                <button
                                    type="button"
                                    onClick={() => onChange([])}
                                    style={{
                                        border: 'none',
                                        background: 'transparent',
                                        padding: 0,
                                        cursor: 'pointer',
                                        fontSize: 12,
                                        fontWeight: 800,
                                        color: 'var(--cpm-text-secondary)',
                                    }}
                                >
                                    {STARTER_COPY.CLEAR}
                                </button>
                            ) : null}
                        </div>
                    ) : null}
                    <div
                        ref={listRef}
                        style={{
                            maxHeight: editor ? 240 : 232,
                            overflowY: 'auto',
                            overscrollBehavior: 'contain',
                            padding: 6,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                        }}
                    >
                        {filtered.map((option) => {
                            const selected = selectedIds.includes(option.id);
                            return (
                                <div
                                    key={option.id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => toggle(option.id)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            toggle(option.id);
                                        }
                                    }}
                                    className="flex items-center"
                                    style={{
                                        gap: 9,
                                        width: '100%',
                                        padding: '8px 9px',
                                        borderRadius: 8,
                                        cursor: 'pointer',
                                        fontSize: 13,
                                        fontWeight: 700,
                                        color: 'var(--cpm-text-primary)',
                                    }}
                                >
                                    <span style={{ pointerEvents: 'none' }}>
                                        <CpmCheckbox checked={selected} readOnly tabIndex={-1} />
                                    </span>
                                    <span style={{ flex: 1, minWidth: 0 }}>{option.name}</span>
                                    {typeof option.count === 'number' ? (
                                        <span
                                            style={{
                                                fontSize: 12,
                                                fontWeight: 800,
                                                color: 'var(--cpm-text-secondary)',
                                                fontVariantNumeric: 'tabular-nums',
                                            }}
                                        >
                                            {option.count}
                                        </span>
                                    ) : null}
                                </div>
                            );
                        })}
                        {filtered.length === 0 ? (
                            <div style={{ padding: '12px 9px', fontSize: 12.5, fontWeight: 600, color: 'var(--cpm-text-secondary)' }}>
                                {STARTER_COPY.NO_STATE_MATCH}
                            </div>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
